/**
 * MarcadorDonut (M10, RF-M10-003, PC-04) — donut SVG dos percentuais por
 * marcador. Renderização client-side pura (sem dependência de lib de gráfico),
 * a partir dos percentuais que o backend garante somarem 100 (PC-04).
 */

import { Marcador } from '../services/painel-types';

export interface MarcadorDonutProps {
  readonly porMarcador: Record<Marcador, number>;
}

const CORES: Record<Marcador, string> = {
  OK: '#147d3f',
  CORRIGIDO: '#2a4b8d',
  ALERTA: '#92610a',
  ERRO: '#a4262c',
};

const ORDEM: readonly Marcador[] = ['OK', 'CORRIGIDO', 'ALERTA', 'ERRO'];

const RAIO = 60;
const CIRCUNFERENCIA = 2 * Math.PI * RAIO;

export function MarcadorDonut({ porMarcador }: MarcadorDonutProps): JSX.Element {
  const total = ORDEM.reduce((acc, m) => acc + (porMarcador[m] ?? 0), 0);

  // Constrói os arcos do donut a partir dos percentuais acumulados.
  let acumulado = 0;
  const arcos = ORDEM.map((m) => {
    const pct = porMarcador[m] ?? 0;
    const dash = (pct / 100) * CIRCUNFERENCIA;
    const offset = (acumulado / 100) * CIRCUNFERENCIA;
    acumulado += pct;
    return { m, pct, dash, offset };
  });

  return (
    <div className="donut-wrap">
      <svg viewBox="0 0 160 160" width="160" height="160" role="img" aria-label="Distribuição por marcador">
        <g transform="rotate(-90 80 80)">
          {total > 0 ? (
            arcos.map((a) => (
              <circle
                key={a.m}
                cx="80"
                cy="80"
                r={RAIO}
                fill="none"
                stroke={CORES[a.m]}
                strokeWidth="20"
                strokeDasharray={`${a.dash} ${CIRCUNFERENCIA - a.dash}`}
                strokeDashoffset={-a.offset}
              />
            ))
          ) : (
            <circle cx="80" cy="80" r={RAIO} fill="none" stroke="#eef0f4" strokeWidth="20" />
          )}
        </g>
      </svg>
      <ul className="donut-legenda">
        {ORDEM.map((m) => (
          <li key={m}>
            <span className="donut-swatch" style={{ background: CORES[m] }} aria-hidden="true" />
            <span>{m}</span>
            <span className="donut-pct">{(porMarcador[m] ?? 0).toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
